import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { checkOverflow } from '../../utils/ids-dom-utils/ids-dom-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLabelStateMixin from '../../mixins/ids-label-state-mixin/ids-label-state-mixin';
import IdsValidationMixin from '../../mixins/ids-validation-mixin/ids-validation-mixin';
import IdsHitboxMixin from '../../mixins/ids-hitbox-mixin/ids-hitbox-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsLabelPositionMixin, { IdsLabelPosition } from '../../mixins/ids-label-position-mixin/ids-label-position-mixin';
import IdsTooltipMixin from '../../mixins/ids-tooltip-mixin/ids-tooltip-mixin';
import IdsElement from '../../core/ids-element';

import '../ids-text/ids-text';
import type IdsText from '../ids-text/ids-text';
import styles from './ids-checkbox.scss';
import { type IdsColorValueCategories } from '../../utils/ids-color-utils/ids-color-utils';
import type IdsTooltip from '../ids-tooltip/ids-tooltip';

const LABEL_ALIGNMENTS = ['start', 'end', 'center', 'justify'];

// Setting defaults label wrap
type IdsCheckboxLabelWrap = 'ellipsis' | 'wrap' | 'ellipsis-no-stretch' | 'wrap-no-stretch';
const LABEL_WRAP_VALUES: IdsCheckboxLabelWrap[] = ['ellipsis', 'wrap', 'ellipsis-no-stretch', 'wrap-no-stretch'];

type IdsCheckboxLabelPosition = Exclude<IdsLabelPosition, 'top'>;

const Base = IdsLabelStateMixin(
  IdsValidationMixin(
    IdsHitboxMixin(
      IdsLabelPositionMixin(
        IdsLocaleMixin(
          IdsTooltipMixin(
            IdsEventsMixin(
              IdsElement
            )
          )
        )
      )
    )
  )
);

/**
 * IDS Checkbox Component
 * @type {IdsCheckbox}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsHitboxMixin
 * @mixes IdsLabelStateMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsTooltipMixin
 * @mixes IdsValidationMixin
 * @part label - the label element
 * @part input - the checkbox input element
 * @part label-checkbox - the label text element
 */
@customElement('ids-checkbox')
@scss(styles)
export default class IdsCheckbox extends Base {
  /**
   * Call the constructor and then initialize
   */
  constructor() {
    super();
  }

  labelAudible?: boolean;

  isFormComponent = true;

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<any> {
    return [
      ...super.attributes,
      attributes.NO_ANIMATION,
      attributes.CHECKED,
      attributes.COLOR,
      attributes.DISABLED,
      attributes.HIDDEN,
      attributes.HORIZONTAL,
      attributes.HORIZONTAL_ALIGNED,
      attributes.INDETERMINATE,
      attributes.LABEL_ALIGN,
      attributes.LABEL_WRAP,
      attributes.VALUE
    ];
  }

  /**
   * Internal change event detection trigger.
   * @private
   * @type {boolean}
   */
  #triggeredChange = false;

  /**
   * Custom Element `connectedCallback` implementation
   * @returns {void}
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.#attachEventHandlers();
    this.#configureTooltip();
  }

  /**
   * Configure tooltip target for the label text
   * @private
   */
  #configureTooltip(): void {
    const labelText = this.shadowRoot?.querySelector<IdsText>('ids-text');
    if (labelText) {
      (labelText as HTMLElement & { onTooltipTargetDetection?: () => HTMLElement | null }).onTooltipTargetDetection = () => labelText.querySelector('span');
    }
  }

  /**
   * Create the Template for the contents.
   * @returns {string} The template.
   */
  template(): string {
    // Checkbox
    const color = this.color ? ` color="${this.color}"` : '';
    const audible = stringToBool(this.labelAudible) ? ' audible="true"' : '';
    const disabled = stringToBool(this.disabled) ? ' disabled' : '';
    const horizontal = stringToBool(this.horizontal) ? ' horizontal' : '';
    const checked = stringToBool(this.checked) ? ' checked' : '';
    const noAnimation = stringToBool(this.noAnimation) ? ' no-animation' : '';
    const rootClass = ` class="ids-checkbox${disabled}${horizontal}${noAnimation}"`;
    let checkboxClass = 'checkbox';
    checkboxClass += stringToBool(this.indeterminate) ? ' indeterminate' : '';
    checkboxClass = ` class="${checkboxClass}"`;
    const rInd = !(stringToBool(this.labelRequired) || this.labelRequired === null);
    const hiddenLabelCss = !this.label.length || this.labelState === 'hidden' ? ' empty' : '';
    const requiredLabelCss = rInd ? ' no-required-indicator' : '';
    const labelWrap = this.labelWrap ? ` label-wrap="${this.labelWrap}"` : '';
    const hasEllipsis = this.labelWrap === 'ellipsis' || this.labelWrap === 'ellipsis-no-stretch';
    const overflow = hasEllipsis ? ' overflow="ellipsis"' : '';

    return `
      <div${rootClass}${color} part="root">
        <label class="ids-label-text${requiredLabelCss}${hiddenLabelCss}" part="label">
          <input id="${this.id}-internal" part="input" type="checkbox"${checkboxClass}${disabled}${checked}>
          <span class="checkmark${checked}" part="checkmark"></span>
          <ids-text${audible}${labelWrap}${overflow} class="label-checkbox" part="label-checkbox"><span>${this.label}</span></ids-text>
        </label>
      </div>
    `;
  }

  /**
   * Attach checkbox change event
   * @private
   * @returns {void}
   */
  attachCheckboxChangeEvent(): void {
    this.onEvent('change', this.input, (e: Event) => {
      this.indeterminate = false;
      this.#triggeredChange = true;
      this.checked = !!this.input?.checked;
      this.triggerEvent(e.type, this, {
        detail: {
          elem: this,
          nativeEvent: e,
          value: this.value,
          checked: this.input?.checked
        },
        bubbles: true
      });
    });
  }

  /**
   * Establish Internal Event Handlers
   * @private
   * @returns {void}
   */
  attachNativeEvents(): void {
    const events = ['focus', 'keydown', 'keypress', 'keyup', 'click', 'dbclick'];
    events.forEach((evt) => {
      this.onEvent(evt, this.input, (e: Event) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      });
    });
  }

  /**
   * Handle events
   * @private
   * @returns {void}
   */
  #attachEventHandlers(): void {
    this.attachCheckboxChangeEvent();
    this.attachNativeEvents();
  }

  /**
   * Get the inner input element
   * @readonly
   * @returns {HTMLInputElement | undefined | null} the inner input element
   */
  get input(): HTMLInputElement | undefined | null {
    return this.container?.querySelector<HTMLInputElement>('input[type="checkbox"]');
  }

  /**
   * Get the inner label element
   * @readonly
   * @returns {HTMLLabelElement | undefined | null} the inner label element
   */
  get labelEl(): HTMLLabelElement | undefined | null {
    return this.container?.querySelector<HTMLLabelElement>('label');
  }

  /**
   * Set the position of the label
   * @param {IdsCheckboxLabelPosition} value The label position value.
   */
  set labelPosition(value: IdsCheckboxLabelPosition | null) {
    super.labelPosition = value;
  }

  /**
   * Get the position of the label
   * @returns {IdsCheckboxLabelPosition} The label position value
   */
  get labelPosition(): IdsCheckboxLabelPosition | null {
    const position = super.labelPosition as IdsLabelPosition | null;
    // Checkboxes don't support 'top', map it to 'inline-end' (default for checkboxes)
    return position === 'top' ? 'inline-end' : position as IdsCheckboxLabelPosition | null;
  }

  /**
   * Sets the checked state to true or false
   * @param {boolean | string} value If true will set `checked` attribute
   */
  set checked(value: boolean | string) {
    const val = stringToBool(value);
    const checkmark = this.shadowRoot?.querySelector<HTMLInputElement>('.checkmark');

    if (!val) this.removeAttribute(attributes.CHECKED);
    if (this.checked === val && this.input?.checked === val) return;

    this.toggleAttribute(attributes.CHECKED, val);
    checkmark?.classList.toggle(attributes.CHECKED, val);

    if (this.input) this.input.checked = val;

    if (val) {
      this.indeterminate = false;
    }

    if (!this.#triggeredChange && this.input) {
      this.triggerEvent('change', this.input, { bubbles: true });
    }

    this.#triggeredChange = false;
  }

  /**
   * Get the checked state
   * @returns {boolean | string} true if the checkbox is checked
   */
  get checked(): boolean | string { return stringToBool(this.getAttribute(attributes.CHECKED)); }

  /**
   * Sets the checkbox color to one of the colors in our color palette for example green
   * @param {IdsColorValueCategories} value If true will set `color` attribute
   */
  set color(value: IdsColorValueCategories) {
    const rootEl = this.shadowRoot?.querySelector('.ids-checkbox');
    if (value) {
      this.setAttribute(attributes.COLOR, value.toString());
      rootEl?.setAttribute(attributes.COLOR, value.toString());
    } else {
      this.removeAttribute(attributes.COLOR);
      rootEl?.removeAttribute(attributes.COLOR);
    }
  }

  /**
   * Get the checkbox color
   * @returns {IdsColorValueCategories} the color value
   */
  get color(): IdsColorValueCategories { return this.getAttribute(attributes.COLOR) as IdsColorValueCategories; }

  /**
   * Sets input to disabled
   * @param {boolean | string} value If true will set `disabled` attribute
   */
  set disabled(value: boolean | string) {
    const rootEl = this.shadowRoot?.querySelector('.ids-checkbox');
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.DISABLED, val.toString());
      this.input?.setAttribute(attributes.DISABLED, val.toString());
      rootEl?.classList.add(attributes.DISABLED);
    } else {
      this.removeAttribute(attributes.DISABLED);
      this.input?.removeAttribute(attributes.DISABLED);
      rootEl?.classList.remove(attributes.DISABLED);
    }
  }

  /**
   * Get the disabled state
   * @returns {boolean | string} true if the checkbox is disabled
   */
  get disabled(): boolean | string { return stringToBool(this.getAttribute(attributes.DISABLED)); }

  /**
   * Sets the hidden state
   * @param {boolean | string} value If true will set `hidden` attribute
   */
  set hidden(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.HIDDEN, '');
      if (this.input) {
        this.input.setAttribute(htmlAttributes.ROLE, 'presentation');
        this.input.setAttribute(attributes.INERT, '');
      }
    } else {
      this.removeAttribute(attributes.HIDDEN);
      if (this.input) {
        this.input.removeAttribute(htmlAttributes.ROLE);
        this.input.removeAttribute(attributes.INERT);
      }
    }
  }

  /**
   * Get the hidden state
   * @returns {boolean} true if the checkbox is hidden
   */
  get hidden(): boolean {
    return this.hasAttribute(attributes.HIDDEN);
  }

  /**
   * Flips the checkbox orientation to horizontal
   * @param {boolean} value If true will set `horizontal` attribute
   */
  set horizontal(value: boolean | string) {
    const rootEl = this.shadowRoot?.querySelector('.ids-checkbox');
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.HORIZONTAL, val.toString());
      rootEl?.classList.add(attributes.HORIZONTAL);
    } else {
      this.removeAttribute(attributes.HORIZONTAL);
      rootEl?.classList.remove(attributes.HORIZONTAL);
    }
  }

  /**
   * Get the horizontal orientation state
   * @returns {boolean | string} true if the checkbox is horizontal
   */
  get horizontal(): boolean | string { return stringToBool(this.getAttribute(attributes.HORIZONTAL)); }

  /**
   * Sets the checkbox to the indeterminate state
   * @param {boolean | string} value The `indeterminate` attribute
   */
  set indeterminate(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.checked = false;
      this.setAttribute(attributes.INDETERMINATE, val.toString());
    } else {
      this.removeAttribute(attributes.INDETERMINATE);
    }
    if (this.input) {
      if (val) {
        this.input.classList.add(attributes.INDETERMINATE);
        this.input.indeterminate = true;
      } else {
        this.input.classList.remove(attributes.INDETERMINATE);
        this.input.indeterminate = false;
      }
    }
  }

  /**
   * Get the indeterminate state
   * @returns {boolean | string} true if the checkbox is indeterminate
   */
  get indeterminate(): boolean | string { return stringToBool(this.getAttribute(attributes.INDETERMINATE)); }

  /**
   * Sets the checkbox `value` attribute
   * @param {string | null} value the value property
   */
  set value(value: string | null) {
    if (!value) {
      this.removeAttribute(attributes.VALUE);
      return;
    }
    this.setAttribute(attributes.VALUE, value || '');
  }

  /**
   * Get the checkbox value attribute
   * @returns {string | null} the value property
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox
   */
  get value(): string | null { return this.checked ? (this.getAttribute(attributes.VALUE) ?? 'on') : ''; }

  /**
   * Disable the check animation
   * @param {boolean | string} value the value property
   */
  set noAnimation(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.NO_ANIMATION, String(val));
      this.container?.classList.add('no-animation');
    } else {
      this.removeAttribute(attributes.NO_ANIMATION);
      this.container?.classList.remove('no-animation');
    }
  }

  /**
   * Get the no animation state
   * @returns {boolean | string} true if animation is disabled
   */
  get noAnimation(): boolean | string { return stringToBool(this.getAttribute(attributes.NO_ANIMATION)) || false; }

  /**
   * Set the label wrap setting of checkbox
   * @param {IdsCheckboxLabelWrap} value ['ellipsis', 'wrap', 'ellipsis-no-stretch', 'wrap-no-stretch']
   */
  set labelWrap(value: IdsCheckboxLabelWrap) {
    if (value && LABEL_WRAP_VALUES.includes(value)) {
      this.setAttribute(attributes.LABEL_WRAP, value);
    } else {
      this.removeAttribute(attributes.LABEL_WRAP);
    }
  }

  /**
   * Get the label wrap setting
   * @returns {IdsCheckboxLabelWrap} the label wrap setting (default 'wrap')
   */
  get labelWrap(): IdsCheckboxLabelWrap {
    const value = this.getAttribute(attributes.LABEL_WRAP) as IdsCheckboxLabelWrap;
    if (value && LABEL_WRAP_VALUES.includes(value)) {
      return value;
    }
    return 'wrap';
  }

  /**
   * Set the label text alignment
   * @param {string} value The alignment value (start, end, center, justify)
   */
  set labelAlign(value: string) {
    // Remove all existing label-align classes
    this.container?.classList.remove(...(this.labelAlignClass(true) as string[]));

    if (LABEL_ALIGNMENTS.includes(value)) {
      this.setAttribute(attributes.LABEL_ALIGN, value);
      this.container?.classList.add(this.labelAlignClass() as string);
    } else {
      this.removeAttribute(attributes.LABEL_ALIGN);
    }
  }

  /**
   * Get the label text alignment
   * @returns {string} The current alignment value
   */
  get labelAlign(): string {
    return this.getAttribute(attributes.LABEL_ALIGN) ?? '';
  }

  /**
   * Get label-align class name/s with prefixed value
   * @param {boolean|undefined} isAll If true, will return all classes as array
   * @returns {string | Array<string>} The class name with prefix
   */
  labelAlignClass(isAll?: boolean): string | Array<string> {
    const prefixed = (v: string) => `label-align-${v}`;
    return isAll ? LABEL_ALIGNMENTS.map((v) => prefixed(v)) : prefixed(this.labelAlign);
  }

  /**
   * Get the inner label text element
   * @readonly
   * @returns {IdsText | null} the inner label text element
   */
  get labelTextEl(): IdsText | null {
    return this.labelEl?.querySelector<IdsText>('.label-checkbox') || null;
  }

  /**
   * Set the tooltip value
   * @param {string} value The tooltip value
   */
  set tooltip(value: string) {
    super.tooltip = value;
  }

  /**
   * Get the tooltip value (defaults to 'true' for ellipsis detection)
   * @returns {string} The tooltip value
   */
  get tooltip(): string {
    return super.tooltip || 'true';
  }

  /**
   * Determines if the tooltip should be shown
   * @param {IdsTooltip | null} _tooltipEl The tooltip element
   * @param {string} tooltipContent The tooltip content
   * @returns {boolean} true if tooltip should show
   */
  canTooltipShow(_tooltipEl: IdsTooltip | null, tooltipContent: string): boolean {
    // Custom tooltip text - always show
    if (tooltipContent && tooltipContent !== 'true') return true;

    // 'true' or no tooltip - check for ellipsis
    const hasEllipsis = this.labelWrap === 'ellipsis' || this.labelWrap === 'ellipsis-no-stretch';
    if (hasEllipsis && this.labelTextEl?.container) {
      return checkOverflow(this.labelTextEl.container) || false;
    }
    return false;
  }

  /**
   * Get the tooltip content
   * @returns {string} The tooltip content
   */
  getTooltipContent(): string {
    return (this.tooltip && this.tooltip !== 'true' && this.tooltip) || this.label || '';
  }

  /**
   * Overrides the standard "focus" behavior to instead pass focus to the inner HTMLInput element.
   */
  focus(): void {
    this.input?.focus();
  }
}
